"use client";

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AnimatePresence, motion } from "motion/react";
import { Icon } from "@iconify-icon/react";
import { ArrowRight, ChevronLeft, Link, Mail } from "lucide-react";

import { type FormValues, LinkInviteTab } from "./LinkInviteTab.tsx";
import { EmailInviteTab } from "./EmailInviteTab.tsx";
import { ConfirmationScreen } from "./ConfirmationScreen.tsx";
import { EmailConfirmationScreen } from "./EmailConfirmationScreen.tsx";

import { StudentSearchResult } from "@/features/workspaces/api/searchStudents";
import { toast } from "@/utils/customToast.tsx";
import { axiosApiInstance } from "@/libs/axios.tsx";
import { API_ENDPOINTS } from "@/constants/api.ts";
import { Dialog, DialogContent } from "@/components/ui/dialog.tsx";
import { Button } from "@/components/ui/button.tsx";
import { ScrollArea } from "@/components/ui/scroll-area.tsx";

interface NewInvitationDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

type StepType = "selection" | "form" | "confirmation";

export function NewInvitationDialog({ isOpen, onClose }: NewInvitationDialogProps) {
  const [submitting, setSubmitting] = useState(false);
  const [invitationId, setInvitationId] = useState<string | null>(null);
  const [emailSent, setEmailSent] = useState<{
    students: StudentSearchResult[];
    description?: string;
  } | null>(null);
  const [selectedStudents, setSelectedStudents] = useState<StudentSearchResult[]>([]);
  const [activeTab, setActiveTab] = useState<"link" | "email">("link");
  const [step, setStep] = useState<StepType>("selection");
  const { t } = useTranslation(["workspaces", "common"]);

  const handleLinkInviteSubmit = async (data: FormValues) => {
    try {
      setSubmitting(true);
      const {
        data: {
          data: { invitation_id },
        },
      } = await axiosApiInstance.post(`${API_ENDPOINTS.WORKSPACES.INVITATIONS.CREATE}`, null, {
        params: {
          description: data.description,
          type: data.type,
        },
      });

      setInvitationId(invitation_id);
      setStep("confirmation");
    } catch (error) {
      console.log(error);
      toast.error(t("common:error"), {
        description: t("common:errorMessage"),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleEmailInviteSubmit = async (data: {
    students: StudentSearchResult[];
    description?: string;
  }) => {
    try {
      setSubmitting(true);

      setEmailSent({
        students: data.students || [],
        description: data.description,
      });
      setStep("confirmation");
      toast.success(t("workspaces:emailInviteSent"));
    } catch (error) {
      console.log(error);
      toast.error(t("common:error"), {
        description: t("common:errorMessage"),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setInvitationId(null);
    setEmailSent(null);
    setSelectedStudents([]);
    setActiveTab("link");
    setStep("selection");
    onClose();
  };

  const proceedToForm = () => {
    setStep("form");
  };

  const goBackToSelection = () => {
    setStep("selection");
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-foreground/80 text-accent-foreground w-fit max-w-7xl overflow-hidden rounded-2xl border-none p-0 shadow-2xl backdrop-blur-xl max-sm:h-dvh max-sm:w-screen max-sm:rounded-none! max-sm:rounded-xl [&_>_button]:z-50!">
        <motion.div
          className="relative h-full"
          initial={false}
          transition={{ duration: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
        >
          <div className="absolute inset-0 opacity-50">
            <div className="absolute -top-10 -right-10 h-32 w-32 rounded-full bg-gradient-to-br from-blue-400/40 to-purple-600/60 max-sm:-top-6 max-sm:-right-6 max-sm:h-20 max-sm:w-20" />
            <div className="absolute -bottom-8 -left-8 h-24 w-24 rounded-full bg-gradient-to-tr from-purple-400/30 to-blue-600/50 max-sm:-bottom-4 max-sm:-left-4 max-sm:h-16 max-sm:w-16" />
          </div>

          <ScrollArea className="h-[min(60dvh,500px)] min-w-[min(100dvw,1000px)] max-sm:h-dvh">
            <div className="relative z-10 h-full min-h-[350px] max-sm:min-h-[calc(100dvh-2rem)]">
              <AnimatePresence initial={false} mode="wait">
                {step === "confirmation" && (invitationId || emailSent) ? (
                  <motion.div
                    key="confirmation"
                    animate={{ opacity: 1 }}
                    className="flex h-full w-full items-center justify-center p-6 max-sm:p-4"
                    exit={{ opacity: 0 }}
                    initial={{ opacity: 0 }}
                    transition={{ duration: 0.25, ease: "easeOut" }}
                  >
                    <div className="w-full max-w-2xl">
                      <motion.div
                        animate={{ opacity: 1, scale: 1 }}
                        initial={{ opacity: 0, scale: 0.95 }}
                        transition={{ duration: 0.3, delay: 0.1, ease: "easeOut" }}
                      >
                        {invitationId ? (
                          <ConfirmationScreen invitationId={invitationId} />
                        ) : (
                          <EmailConfirmationScreen
                            description={emailSent?.description}
                            students={emailSent?.students || []}
                          />
                        )}
                      </motion.div>
                    </div>
                  </motion.div>
                ) : step === "form" ? (
                  <motion.div
                    key="form"
                    animate={{ opacity: 1 }}
                    className="flex h-full w-full flex-col p-6 max-sm:p-4"
                    exit={{ opacity: 0 }}
                    initial={{ opacity: 0 }}
                    transition={{ duration: 0.25, ease: "easeOut" }}
                  >
                    {/* Back Button */}
                    <motion.div
                      animate={{ opacity: 1 }}
                      className="mb-4 flex-shrink-0 max-sm:mb-3"
                      initial={{ opacity: 0 }}
                      transition={{ duration: 0.2, delay: 0.1, ease: "easeOut" }}
                    >
                      <Button
                        className="hover:bg-accent-foreground/10 text-accent-foreground/80 hover:text-accent-foreground transition-colors duration-150"
                        size="sm"
                        variant="ghost"
                        onClick={goBackToSelection}
                      >
                        <ChevronLeft className="mr-2 h-4 w-4" />
                        {t("common:back")}
                      </Button>
                    </motion.div>

                    {/* Form Content */}
                    <motion.div
                      animate={{ opacity: 1 }}
                      className="grid flex-1 grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-8"
                      initial={{ opacity: 0 }}
                      transition={{ duration: 0.25, delay: 0.15, ease: "easeOut" }}
                    >
                      <div className="flex h-full flex-col">
                        {activeTab === "link" ? (
                          <LinkInviteTab
                            submitting={submitting}
                            onSubmit={handleLinkInviteSubmit}
                          />
                        ) : (
                          <EmailInviteTab
                            submitting={submitting}
                            onStudentsChange={setSelectedStudents}
                            onSubmit={handleEmailInviteSubmit}
                          />
                        )}
                      </div>

                      {/* Right side information panel */}
                      <div className="flex flex-col space-y-3 max-sm:order-first">
                        {activeTab === "email" ? (
                          <>
                            {/* Students Area */}
                            <motion.div
                              className="bg-side-accent flex h-full flex-col rounded-xl p-3 backdrop-blur-sm max-sm:p-2"
                              transition={{ duration: 0.2, ease: "easeOut" }}
                            >
                              {/* Students List Area */}
                              <div className="flex h-full flex-col">
                                {selectedStudents.length === 0 ? (
                                  <div className="flex h-full flex-col items-center justify-center text-center">
                                    <Icon
                                      className="text-muted-foreground mb-2 h-6 w-6"
                                      icon="tabler:users-off"
                                    />
                                    <p className="text-muted-foreground text-xs">
                                      {t("workspaces:noStudentsSelected")}
                                    </p>
                                    <p className="text-muted-foreground text-xs">
                                      {t("workspaces:searchStudentsAbove")}
                                    </p>
                                  </div>
                                ) : (
                                  <div className="flex h-full flex-col space-y-2">
                                    {selectedStudents.map((student) => (
                                      <motion.div
                                        key={student.id}
                                        animate={{ opacity: 1, scale: 1 }}
                                        className="group bg-muted/50 relative cursor-pointer overflow-hidden rounded-lg p-3 backdrop-blur-sm transition-all duration-300"
                                        initial={{ opacity: 0, scale: 0.95 }}
                                        transition={{ duration: 0.2, ease: "easeOut" }}
                                      >
                                        <div className="absolute inset-0 top-1/2 -left-0 h-24 w-24 -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-full bg-gradient-to-br from-purple-400 to-indigo-500 opacity-25">
                                          <img
                                            alt={student.name}
                                            className="h-full w-full object-cover blur-2xl"
                                            src={
                                              student.avatar_path ||
                                              "/placeholder.svg?height=40&width=40"
                                            }
                                          />
                                        </div>
                                        <div className="flex items-center gap-2.5">
                                          <div className="bg-muted/30 flex h-8 w-8 flex-shrink-0 items-center justify-center overflow-hidden rounded-md">
                                            <img
                                              alt={student.name}
                                              className="h-full w-full object-cover"
                                              src={
                                                student.avatar_path ||
                                                "/placeholder.svg?height=40&width=40"
                                              }
                                            />
                                          </div>
                                          <div className="min-w-0 flex-1">
                                            <h4 className="text-accent-foreground truncate text-xs font-medium">
                                              {student.name}
                                            </h4>
                                            <p className="text-muted-foreground text-[0.65rem]">
                                              {t("common:roles.student")}
                                            </p>
                                          </div>
                                          <Button
                                            className="h-6 w-6 p-0 hover:bg-red-500/10 hover:text-red-600"
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => {
                                              setSelectedStudents((prev) =>
                                                prev.filter((s) => s.id !== student.id),
                                              );
                                            }}
                                          >
                                            <Icon className="h-3 w-3" icon="tabler:x" />
                                          </Button>
                                        </div>
                                      </motion.div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </motion.div>

                            {/* Tips Section */}
                            <motion.div
                              animate={{
                                backgroundColor: "rgba(139, 92, 246, 0.05)",
                                borderColor: "rgba(139, 92, 246, 0.1)",
                              }}
                              className="flex flex-col space-y-2 rounded-xl bg-gradient-to-br p-3 backdrop-blur-sm max-sm:p-2"
                              transition={{ duration: 0.2, ease: "easeOut" }}
                            >
                              <ul className="text-accent-foreground/70 space-y-2 text-xs">
                                <li className="flex items-start gap-3">
                                  <Icon
                                    className="mt-1 flex-shrink-0 text-purple-500"
                                    height="0.625rem"
                                    icon="tabler:check"
                                    width="0.625rem"
                                  />
                                  <span className="leading-relaxed">
                                    {t("workspaces:emailInviteTip1")}
                                  </span>
                                </li>
                                <li className="flex items-start gap-3">
                                  <Icon
                                    className="mt-1 flex-shrink-0 text-purple-500"
                                    height="0.625rem"
                                    icon="tabler:check"
                                    width="0.625rem"
                                  />
                                  <span className="leading-relaxed">
                                    {t("workspaces:emailInviteTip2")}
                                  </span>
                                </li>
                              </ul>
                            </motion.div>
                          </>
                        ) : (
                          <motion.div
                            className="mb-6 flex h-full flex-col space-y-3 rounded-xl p-4 max-sm:p-3"
                            transition={{ duration: 0.2, ease: "easeOut" }}
                          >
                            <div className="text-center">
                              <motion.div
                                animate={{
                                  backgroundColor: "rgba(59, 130, 246, 0.2)",
                                }}
                                className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full backdrop-blur-sm max-sm:h-12 max-sm:w-12"
                                transition={{ duration: 0.2, ease: "easeOut" }}
                              >
                                <motion.div
                                  key={activeTab}
                                  animate={{ scale: 1, opacity: 1 }}
                                  className={"flex items-center"}
                                  initial={{ scale: 0.8, opacity: 0 }}
                                  transition={{ duration: 0.15, ease: "easeOut" }}
                                >
                                  <Icon
                                    className="text-accent-foreground"
                                    height="2rem"
                                    icon="tabler:link"
                                    width="2rem"
                                  />
                                </motion.div>
                              </motion.div>
                              <motion.h3
                                key={`title-${activeTab}`}
                                animate={{ opacity: 1 }}
                                className="text-accent-foreground mb-3 text-xl font-semibold max-sm:text-lg"
                                initial={{ opacity: 0 }}
                                transition={{ duration: 0.15, delay: 0.05, ease: "easeOut" }}
                              >
                                {activeTab === "link"
                                  ? t("workspaces:linkInvite")
                                  : t("workspaces:emailInvite")}
                              </motion.h3>
                              <motion.p
                                key={`desc-${activeTab}`}
                                animate={{ opacity: 1 }}
                                className="text-accent-foreground/70 text-base max-sm:text-sm"
                                initial={{ opacity: 0 }}
                                transition={{ duration: 0.15, delay: 0.1, ease: "easeOut" }}
                              >
                                {activeTab === "link"
                                  ? t("workspaces:linkInviteDescription")
                                  : t("workspaces:emailInviteDescription")}
                              </motion.p>
                            </div>

                            <div className="flex flex-1 flex-col justify-center">
                              <div className="rounded-xl bg-gradient-to-br from-blue-500/5 to-purple-600/5 p-6 max-sm:p-4">
                                <ul className="text-accent-foreground/70 space-y-3 text-base max-sm:space-y-2 max-sm:text-sm">
                                  <li className="flex items-start gap-3">
                                    <Icon
                                      className="mt-1 flex-shrink-0 text-blue-500"
                                      height="1rem"
                                      icon="tabler:check"
                                      width="1rem"
                                    />
                                    <span className="leading-relaxed">
                                      {t("workspaces:invitationTip1")}
                                    </span>
                                  </li>
                                  <li className="flex items-start gap-3">
                                    <Icon
                                      className="mt-1 flex-shrink-0 text-blue-500"
                                      height="1rem"
                                      icon="tabler:check"
                                      width="1rem"
                                    />
                                    <span className="leading-relaxed">
                                      {t("workspaces:invitationTip2")}
                                    </span>
                                  </li>
                                </ul>
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </div>
                    </motion.div>
                  </motion.div>
                ) : (
                  <motion.div
                    key="selection"
                    animate={{ opacity: 1 }}
                    className="flex h-full w-full flex-col justify-center p-8 max-sm:p-4"
                    exit={{ opacity: 0 }}
                    initial={{ opacity: 0 }}
                    transition={{ duration: 0.25, ease: "easeOut" }}
                  >
                    <div className="grid h-full grid-cols-1 gap-8 lg:grid-cols-5 lg:gap-10">
                      {/* Image - Top on mobile, right on desktop */}
                      <motion.div
                        animate={{ opacity: 1, scale: 1 }}
                        className="order-1 col-span-2 flex items-center justify-center lg:order-2"
                        initial={{ opacity: 0, scale: 0.95 }}
                        transition={{ duration: 0.3, delay: 0.1, ease: "easeOut" }}
                      >
                        <div className="h-full max-h-48 w-full lg:max-h-none">
                          <img
                            alt={t("workspaces:inviteMembers")}
                            className="h-full w-full object-contain"
                            src="https://public-assets.ximly.app/tutor-invites.webp"
                          />
                        </div>
                      </motion.div>

                      {/* Content - Bottom on mobile, left on desktop */}
                      <motion.div
                        animate={{ opacity: 1 }}
                        className="order-2 col-span-3 flex flex-col justify-center lg:order-1"
                        initial={{ opacity: 0 }}
                        transition={{ duration: 0.25, delay: 0.15, ease: "easeOut" }}
                      >
                        {/* Header Section */}
                        <motion.div
                          animate={{ opacity: 1 }}
                          className="mt-2 mb-8 max-sm:mb-6"
                          initial={{ opacity: 0 }}
                          transition={{ duration: 0.25, delay: 0.2, ease: "easeOut" }}
                        >
                          <h2 className="text-accent-foreground mb-4 text-2xl font-bold max-sm:mb-3 max-sm:text-2xl">
                            {t("workspaces:inviteMembers")}
                          </h2>
                          <p className="text-accent-foreground/70 leading-relaxed max-sm:text-base">
                            {t("workspaces:inviteMembersDescription")}
                          </p>
                        </motion.div>

                        {/* Selection Options */}
                        <motion.div
                          animate={{ opacity: 1 }}
                          className="flex flex-1 flex-col justify-center"
                          initial={{ opacity: 0 }}
                          transition={{ duration: 0.25, delay: 0.25, ease: "easeOut" }}
                        >
                          <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-2 lg:gap-6">
                            <motion.button
                              className={`group relative overflow-hidden rounded-2xl p-4 text-left transition-all duration-200 max-sm:p-6 ${
                                activeTab === "link"
                                  ? "bg-gradient-to-br from-blue-500/20 to-purple-600/20 shadow-xl"
                                  : "bg-accent-foreground/5 hover:border-blue-500/30 hover:bg-gradient-to-br hover:from-blue-500/10 hover:to-purple-600/10 hover:shadow-lg"
                              }`}
                              transition={{ duration: 0.15, ease: "easeOut" }}
                              onClick={() => setActiveTab("link")}
                            >
                              <div className="flex flex-col items-center gap-3 max-sm:gap-2">
                                <motion.div
                                  animate={{
                                    backgroundColor:
                                      activeTab === "link"
                                        ? "rgba(59, 130, 246, 0.3)"
                                        : "rgba(255, 255, 255, 0.1)",
                                    scale: activeTab === "link" ? 1.05 : 1,
                                  }}
                                  className="flex h-12 w-12 items-center justify-center rounded-xl transition-colors max-sm:h-10 max-sm:w-10"
                                  transition={{ duration: 0.15, ease: "easeOut" }}
                                >
                                  <Link className="text-accent-foreground h-6 w-6 max-sm:h-5 max-sm:w-5" />
                                </motion.div>
                                <div className="text-center">
                                  <h3 className="text-accent-foreground mb-2 text-lg font-semibold max-sm:text-base">
                                    {t("workspaces:linkInvite")}
                                  </h3>
                                  <p className="text-accent-foreground/70 text-sm">
                                    {t("workspaces:linkInviteDescription")}
                                  </p>
                                </div>
                              </div>
                              <AnimatePresence>
                                {activeTab === "link" && (
                                  <motion.div
                                    animate={{ scale: 1, opacity: 1 }}
                                    className="absolute top-4 right-4 max-sm:top-3 max-sm:right-3"
                                    exit={{ scale: 0, opacity: 0 }}
                                    initial={{ scale: 0, opacity: 0 }}
                                    transition={{ duration: 0.15, ease: "easeOut" }}
                                  >
                                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-500/30 max-sm:h-5 max-sm:w-5">
                                      <Icon
                                        className="text-blue-500"
                                        height="0.875rem"
                                        icon="tabler:check"
                                        width="0.875rem"
                                      />
                                    </div>
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </motion.button>

                            <motion.button
                              className={`group relative overflow-hidden rounded-2xl p-4 text-left transition-all duration-200 max-sm:p-6 ${
                                activeTab === "email"
                                  ? "bg-gradient-to-br from-purple-500/20 to-indigo-600/20 shadow-xl"
                                  : "bg-accent-foreground/5 hover:border-purple-500/30 hover:bg-gradient-to-br hover:from-purple-500/10 hover:to-indigo-600/10 hover:shadow-lg"
                              }`}
                              transition={{ duration: 0.15, ease: "easeOut" }}
                              onClick={() => setActiveTab("email")}
                            >
                              <div className="flex flex-col items-center gap-3 max-sm:gap-2">
                                <motion.div
                                  animate={{
                                    backgroundColor:
                                      activeTab === "email"
                                        ? "rgba(139, 92, 246, 0.3)"
                                        : "rgba(255, 255, 255, 0.1)",
                                    scale: activeTab === "email" ? 1.05 : 1,
                                  }}
                                  className="flex h-12 w-12 items-center justify-center rounded-xl transition-colors max-sm:h-10 max-sm:w-10"
                                  transition={{ duration: 0.15, ease: "easeOut" }}
                                >
                                  <Mail className="text-accent-foreground h-6 w-6 max-sm:h-5 max-sm:w-5" />
                                </motion.div>
                                <div className="text-center">
                                  <h3 className="text-accent-foreground mb-2 text-lg font-semibold max-sm:text-base">
                                    {t("workspaces:emailInvite")}
                                  </h3>
                                  <p className="text-accent-foreground/70 text-sm">
                                    {t("workspaces:emailInviteDescription")}
                                  </p>
                                </div>
                              </div>
                              <AnimatePresence>
                                {activeTab === "email" && (
                                  <motion.div
                                    animate={{ scale: 1, opacity: 1 }}
                                    className="absolute top-4 right-4 max-sm:top-3 max-sm:right-3"
                                    exit={{ scale: 0, opacity: 0 }}
                                    initial={{ scale: 0, opacity: 0 }}
                                    transition={{ duration: 0.15, ease: "easeOut" }}
                                  >
                                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-purple-500/30 max-sm:h-5 max-sm:w-5">
                                      <Icon
                                        className="text-purple-500"
                                        height="0.875rem"
                                        icon="tabler:check"
                                        width="0.875rem"
                                      />
                                    </div>
                                  </motion.div>
                                )}
                              </AnimatePresence>
                            </motion.button>
                          </div>

                          {/* Continue Button */}
                          <motion.div
                            animate={{ opacity: 1 }}
                            className="mt-8 w-full max-sm:mt-6"
                            initial={{ opacity: 0 }}
                            transition={{ duration: 0.25, delay: 0.3, ease: "easeOut" }}
                          >
                            <Button
                              className="w-full"
                              size="lg"
                              variant={"foreground"}
                              onClick={proceedToForm}
                            >
                              {t("common:continue")}
                              <ArrowRight className="ml-2 h-4 w-4" />
                            </Button>
                          </motion.div>
                        </motion.div>
                      </motion.div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </ScrollArea>
        </motion.div>
      </DialogContent>
    </Dialog>
  );
}
